import { randomUUID } from 'node:crypto';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { getContext } from '@adornis/baseql/server/context.js';
import { type AuthenticationContext, refreshAccountAccess, isAuthenticatedContext } from '../server/auth.js';
import { CallSession } from './CallSession.js';
import type {
  WorkTask,
  WorkTaskAccess,
  WorkTaskApproval,
  WorkTaskFeedbackLink,
  WorkTaskPriority,
  WorkTaskStatus,
  WorkTaskBillingDisposition,
  LegacyWorkTaskStatus,
} from './work-task-types.js';
import { taskHours, taskEstimate, parseTaskEstimate, estimateTotal } from './work-task-estimates.js';
import {
  hasCurrentTaskApproval,
  taskWorkflowStatus,
  legacyTaskStatus,
  taskKind,
  taskCalendarDate,
  taskTransition,
  WORK_TASK_STATUSES,
} from './work-task-workflow.js';
import {
  approvedWorkTaskMinutes,
  buildWorkTaskBillingReport,
  buildWorkTaskBillingSummary,
  hasWorkTaskPayment,
} from './work-task-billing.js';

// Intentionally not registered as MongoEntity: there are no generic CRUD resolvers
// that could bypass the approval checks or write a forged approval/history.
const COLLECTION = 'work_tasks';
const MAX_TIME_MS = 5000;
const LIST_LIMIT = 1000;
const BILLING_EXPORT_LIMIT = 10000;
type Estimate = Pick<
  WorkTask,
  | 'title'
  | 'description'
  | 'developmentHours'
  | 'testingHours'
  | 'estimate'
  | 'deliveryEstimate'
  | 'feedbackLinks'
  | 'kind'
  | 'targetReleaseDate'
>;
interface TaskRow extends Omit<WorkTask, 'status'> {
  status: LegacyWorkTaskStatus;
  workflowStatus?: WorkTaskStatus;
  createdBy: string;
  updatedBy: string;
  approvalHistory: Array<WorkTaskApproval & { snapshot: Estimate }>;
  archiveHistory?: Array<{ archived: boolean; at: string; byUserID: string }>;
  technicalProgress?: { status: WorkTaskStatus; revision: number };
  workflowHistory?: Array<{
    from: WorkTaskStatus;
    to: WorkTaskStatus;
    at: string;
    byUserID: string;
  }>;
  billingHistory?: Array<{
    disposition: WorkTaskBillingDisposition;
    approvedMinutes: number | null;
    source: string;
    agreedOn: string | null;
    billingDate: string | null;
    at: string;
    byUserID: string;
  }>;
  paymentHistory?: Array<{
    paid: boolean;
    approvedMinutes: number;
    at: string;
    byUserID: string;
  }>;
}
// A counter and unique index, scoped to the application's configured database, protect IDs
// across parallel requests/processes. Gaps are allowed; allocated numbers are never reused.
interface TaskCounter {
  _id: string;
  value: number;
}
let initialization: Promise<void> | null = null;
async function allocateTaskNumber(): Promise<string> {
  const counters = await getRawCollection<TaskCounter>('work_task_counters');
  const result = await counters.findOneAndUpdate(
    { _id: 'task-number' },
    { $inc: { value: 1 } },
    { returnDocument: 'after', maxTimeMS: MAX_TIME_MS },
  );
  // MongoDB driver 5 returns ModifyResult; driver 6 returns the document directly.
  const raw = result as unknown as {
    value?: number | { value?: number };
  } | null;
  const value = typeof raw?.value === 'number' ? raw.value : raw?.value?.value;
  if (!Number.isSafeInteger(value) || value! < 1) throw new Error('TASK_NUMBER_ALLOCATION_FAILED');
  return 'ZW-' + String(value).padStart(5, '0');
}
async function initializeTaskStorage(): Promise<void> {
  const collection = await getRawCollection<TaskRow>(COLLECTION);
  await collection.createIndex(
    { taskNumber: 1 },
    {
      name: 'work_task_number_unique',
      unique: true,
      partialFilterExpression: { taskNumber: { $type: 'string' } },
    },
  );
  const existing: TaskRow[] = [];
  let after = '',
    maxNumber = 0;
  while (true) {
    const batch = await collection
      .find(after ? { _id: { $gt: after } } : {}, { maxTimeMS: MAX_TIME_MS })
      .sort({ _id: 1 })
      .limit(500)
      .toArray();
    if (!batch.length) break;
    existing.push(...batch);
    if (existing.length > BILLING_EXPORT_LIMIT) throw new Error('TASK_MIGRATION_TOO_LARGE');
    after = batch[batch.length - 1]!._id;
    for (const row of batch) {
      if (row.taskNumber !== undefined) {
        if (!/^ZW-\d{5,}$/.test(row.taskNumber)) throw new Error('TASK_INVALID_STORED_NUMBER');
        const value = Number(row.taskNumber.slice(3));
        if (!Number.isSafeInteger(value) || value < 1) throw new Error('TASK_INVALID_STORED_NUMBER');
        maxNumber = Math.max(maxNumber, value);
      }
    }
  }
  const counters = await getRawCollection<TaskCounter>('work_task_counters');
  try {
    await counters.updateOne(
      { _id: 'task-number' },
      { $max: { value: maxNumber } },
      { upsert: true, maxTimeMS: MAX_TIME_MS },
    );
  } catch (error) {
    if ((error as { code?: number }).code !== 11000) throw error;
    await counters.updateOne({ _id: 'task-number' }, { $max: { value: maxNumber } }, { maxTimeMS: MAX_TIME_MS });
  }
  for (const initialRow of existing) {
    let row: TaskRow | null = initialRow;
    let allocatedNumber: string | undefined;
    let complete = false;
    for (let attempt = 0; attempt < 5; attempt++) {
      // A deletion during migration must not recreate the task.
      if (!row) {
        complete = true;
        break;
      }
      const logicalStatus = taskWorkflowStatus(row);
      const workflowStatus = logicalStatus === 'production-completed' ? 'ready-for-production' : logicalStatus;
      if (row.taskNumber && row.workflowStatus === workflowStatus) {
        complete = true;
        break;
      }
      const taskNumber = row.taskNumber || (allocatedNumber ??= await allocateTaskNumber());
      // Metadata-only migration: do not alter revision, timestamps, approval or history.
      const result = await collection.updateOne(
        {
          _id: row._id,
          revision: row.revision,
          ...(row.taskNumber ? { taskNumber: row.taskNumber } : { taskNumber: { $exists: false } }),
          ...(row.workflowStatus ? { workflowStatus: row.workflowStatus } : { workflowStatus: { $exists: false } }),
        },
        { $set: { taskNumber, workflowStatus } },
        { maxTimeMS: MAX_TIME_MS },
      );
      if (result.matchedCount === 1) {
        complete = true;
        break;
      }
      // Another request may have edited the legacy row or migrated it first. Read its
      // latest state and retry; never mark initialization complete with missing metadata.
      row = await collection.findOne({ _id: initialRow._id }, { maxTimeMS: MAX_TIME_MS });
    }
    if (!complete) throw new Error('TASK_MIGRATION_CONFLICT');
  }
}
async function ensureTaskStorage(): Promise<void> {
  if (!initialization)
    initialization = initializeTaskStorage().catch(error => {
      initialization = null;
      throw error;
    });
  return initialization;
}
function inputText(value: unknown, max: number, required = false): string {
  if (Object.prototype.toString.call(value) !== '[object String]') throw new Error('TASK_INVALID_INPUT');
  const text = String(value).trim();
  if (text.length > max || (required && !text) || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(text))
    throw new Error('TASK_INVALID_INPUT');
  return text;
}
function taskID(value: unknown): string {
  return inputText(value, 256, true);
}
function revisionNumber(value: unknown): number {
  if (Object.prototype.toString.call(value) !== '[object Number]') throw new Error('TASK_INVALID_INPUT');
  const revision = Number(value);
  if (!Number.isSafeInteger(revision) || revision < 1) throw new Error('TASK_INVALID_INPUT');
  return revision;
}
function priorityValue(value: unknown): WorkTaskPriority {
  if (value !== 'high' && value !== 'medium' && value !== 'low') throw new Error('TASK_INVALID_INPUT');
  return value;
}
function booleanValue(value: unknown): boolean {
  // BaseQL wraps scalar parameters (including false) in their native constructors.
  if (Object.prototype.toString.call(value) !== '[object Boolean]') throw new Error('TASK_INVALID_INPUT');
  return Boolean.prototype.valueOf.call(value);
}
async function taskAccess(): Promise<WorkTaskAccess & { userID: string }> {
  const context = getContext() as AuthenticationContext | undefined;
  if (!context || context.serverContext || typeof context.userID !== 'string' || !context.userID)
    throw new Error('TASK_FORBIDDEN');
  await refreshAccountAccess(context);
  if (!isAuthenticatedContext(context)) throw new Error('TASK_FORBIDDEN');
  // The selected approver is a manually recorded business attribution.
  // userID separately records the real account that saved the approval.
  return {
    userID: context.userID,
    canManage: true,
    canApprove: true,
    approverName: null,
  };
}
async function taskManager() {
  const access = await taskAccess();
  if (!access.canManage) throw new Error('TASK_FORBIDDEN');
  return access;
}
async function taskAssignee(value: unknown): Promise<NonNullable<WorkTask['assignee']> | null> {
  if (value === null) return null;
  const id = taskID(value);
  const users = await getRawCollection('adornisuser');
  const user = await users.findOne({ _id: id } as any, {
    projection: { _id: 1, username: 1, archivedAt: 1 },
    maxTimeMS: MAX_TIME_MS,
  });
  if (!user || user.archivedAt || typeof user.username !== 'string' || !user.username.trim())
    throw new Error('TASK_INVALID_ASSIGNEE');
  return { userID: id, name: user.username.slice(0, 180) };
}
function publicTask(row: TaskRow): WorkTask {
  const {
    _id,
    title,
    description,
    developmentHours,
    testingHours,
    deliveryEstimate,
    revision,
    estimateRevision,
    feedbackLinks,
    approval,
    createdAt,
    updatedAt,
  } = row;
  return {
    taskNumber: row.taskNumber,
    kind: row.kind ?? 'unclassified',
    targetReleaseDate: row.targetReleaseDate ?? null,
    billingHours: row.billingHours ?? null,
    billingDate: row.billingDate ?? null,
    archivedAt: row.archivedAt ?? null,
    completedAt: row.completedAt ?? null,
    billingDisposition: row.billingDisposition ?? 'undecided',
    approvedBillableMinutes: row.approvedBillableMinutes ?? null,
    billingAgreement: row.billingAgreement ?? null,
    payment: row.payment ?? null,
    productionCompletion: row.productionCompletion ?? null,
    testInstructions: row.testInstructions ?? '',
    assignee: row.assignee ?? null,
    estimate: taskEstimate(row),
    priority: row.priority ?? 'medium',
    pinned: row.pinned === true,
    _id,
    title,
    description,
    developmentHours,
    testingHours,
    deliveryEstimate,
    status: taskWorkflowStatus(row),
    revision,
    estimateRevision,
    feedbackLinks,
    approval,
    createdAt,
    updatedAt,
  };
}
async function currentTask(id: unknown, revision: unknown, initialize = true): Promise<TaskRow> {
  if (initialize) await ensureTaskStorage();
  const collection = await getRawCollection<TaskRow>(COLLECTION);
  const row = await collection.findOne(
    { _id: taskID(id), revision: revisionNumber(revision) },
    { maxTimeMS: MAX_TIME_MS },
  );
  if (!row) throw new Error('TASK_CONFLICT');
  return row;
}
async function updateTask(row: TaskRow, userID: string, change: Partial<TaskRow>): Promise<string> {
  const collection = await getRawCollection<TaskRow>(COLLECTION);
  const next = {
    ...row,
    ...change,
    revision: row.revision + 1,
    updatedAt: new Date().toISOString(),
    updatedBy: userID,
  };
  const explicitStatus = change.workflowStatus !== undefined || change.status !== undefined;
  const status =
    change.technicalProgress?.status ??
    (explicitStatus ? taskWorkflowStatus({ ...next, technicalProgress: undefined }) : taskWorkflowStatus(row));
  next.technicalProgress = { status, revision: next.revision };
  if (explicitStatus && status !== 'production-completed') next.productionCompletion = null;
  const { _id, ...fields } = next;
  const result = await collection.updateOne(
    { _id, revision: row.revision },
    { $set: fields },
    { maxTimeMS: MAX_TIME_MS },
  );
  if (result.matchedCount !== 1) throw new Error('TASK_CONFLICT');
  return JSON.stringify(publicTask(next));
}
async function feedbackSource(value: unknown): Promise<WorkTaskFeedbackLink> {
  const id = taskID(value);
  const sessions = await getCollection(CallSession);
  const row = await sessions.findOne(
    { $or: [{ sessionID: id }, { callUuid: id }] },
    {
      projection: { sessionID: 1, callUuid: 1, testerName: 1, testerNotes: 1 },
      maxTimeMS: MAX_TIME_MS,
    },
  );
  if (!row || typeof row.testerNotes !== 'string' || !row.testerNotes.trim())
    throw new Error('TASK_FEEDBACK_NOT_FOUND');
  return {
    callID: String(row.sessionID || row.callUuid),
    testerName: String(row.testerName || '').slice(0, 100),
    notesPreview: row.testerNotes.trim().slice(0, 500),
    notes: row.testerNotes.trim().slice(0, 12000),
    notesTruncated: row.testerNotes.trim().length > 12000,
  };
}
function estimateSnapshot(row: Estimate): Estimate {
  return {
    title: row.title,
    description: row.description,
    developmentHours: row.developmentHours,
    testingHours: row.testingHours,
    estimate: taskEstimate(row),
    deliveryEstimate: row.deliveryEstimate,
    feedbackLinks: row.feedbackLinks,
    kind: row.kind ?? 'unclassified',
    targetReleaseDate: row.targetReleaseDate ?? null,
  };
}
export const workTaskList = registerQuery({
  type: () => String,
  operationName: 'workTaskList',
  params: [],
  resolve: () => async () => {
    const access = await taskAccess();
    const users = await getRawCollection('adornisuser');
    const accounts = await users
      .find({ archivedAt: null }, { projection: { _id: 1, username: 1 }, maxTimeMS: MAX_TIME_MS })
      .sort({ username: 1 })
      .limit(1000)
      .toArray();
    const assignees = accounts
      .filter(account => typeof account.username === 'string' && account.username.trim())
      .map(account => ({
        userID: String(account._id),
        name: String(account.username).slice(0, 180),
      }));
    await ensureTaskStorage();
    const collection = await getRawCollection<TaskRow>(COLLECTION);
    const rows = await collection
      .find(
        {},
        {
          projection: {
            approvalHistory: 0,
            archiveHistory: 0,
            billingHistory: 0,
            paymentHistory: 0,
            workflowHistory: 0,
            createdBy: 0,
            updatedBy: 0,
          },
          maxTimeMS: MAX_TIME_MS,
        },
      )
      .sort({ pinned: -1, updatedAt: -1, _id: 1 })
      .limit(BILLING_EXPORT_LIMIT + 1)
      .toArray();
    if (rows.length > BILLING_EXPORT_LIMIT) throw new Error('TASK_BILLING_EXPORT_TOO_LARGE');
    const tasks = rows.map(publicTask);
    return JSON.stringify({
      tasks: tasks.slice(0, LIST_LIMIT),
      access,
      assignees,
      truncated: rows.length > LIST_LIMIT,
      billingSummary: buildWorkTaskBillingSummary(tasks),
    });
  },
});
export const workTaskSave = registerMutation({
  type: () => String,
  operationName: 'workTaskSave',
  params: [{ name: 'payloadJSON', type: () => String }],
  resolve: (payloadJSON: string) => async () => {
    const access = await taskManager();
    const raw = inputText(payloadJSON, 40000, true);
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(raw);
    } catch {
      throw new Error('TASK_INVALID_INPUT');
    }
    const allowed = [
      '_id',
      'expectedRevision',
      'title',
      'description',
      'developmentHours',
      'testingHours',
      'estimate',
      'priority',
      'pinned',
      'deliveryEstimate',
      'feedbackCallIDs',
      'kind',
      'targetReleaseDate',
      'billingHours',
      'billingDate',
      'testInstructions',
      'assigneeUserID',
    ];
    if (
      !data ||
      typeof data !== 'object' ||
      Array.isArray(data) ||
      Object.keys(data).some(key => !allowed.includes(key))
    )
      throw new Error('TASK_INVALID_INPUT');
    if (data.estimate !== undefined && (data.developmentHours !== undefined || data.testingHours !== undefined))
      throw new Error('TASK_INVALID_INPUT');
    const estimate =
      data.estimate !== undefined
        ? parseTaskEstimate(data.estimate)
        : taskEstimate({
            developmentHours: taskHours(data.developmentHours),
            testingHours: taskHours(data.testingHours),
          });
    // Old clients do not know classification/billing. Reject instead of losing new fields.
    if (['kind', 'targetReleaseDate', 'billingHours', 'billingDate'].some(key => !Object.hasOwn(data, key)))
      throw new Error('TASK_RELOAD_REQUIRED');
    const kind = taskKind(data.kind, data._id !== undefined);
    const billingHours = data.billingHours === null ? null : taskHours(data.billingHours);
    const billingDate = taskCalendarDate(data.billingDate);
    if (kind !== 'feature' && (billingHours !== null || billingDate !== null)) throw new Error('TASK_INVALID_INPUT');
    const billing = { billingHours, billingDate };
    const fields = {
      kind,
      targetReleaseDate: taskCalendarDate(data.targetReleaseDate),
      title: inputText(data.title, 180, true),
      description: inputText(data.description, 8000),
      estimate,
      // Retained for old records/clients; the estimate object is authoritative.
      developmentHours: estimate.mode === 'breakdown' ? estimate.development.min : 0,
      testingHours: estimate.mode === 'breakdown' ? estimate.testing.min : 0,
      deliveryEstimate: inputText(data.deliveryEstimate, 500),
    };
    if (data._id !== undefined) {
      if (data.feedbackCallIDs !== undefined) throw new Error('TASK_INVALID_INPUT');
      const row = await currentTask(data._id, data.expectedRevision);
      if (row.payment && kind !== row.kind) throw new Error('TASK_BILLING_PAID_LOCKED');
      if (
        kind !== row.kind &&
        row.archivedAt &&
        approvedWorkTaskMinutes({ ...publicTask(row), kind }) !== null &&
        !hasWorkTaskPayment(row)
      )
        throw new Error('TASK_BILLING_UNPAID_ARCHIVE');
      // An older client cannot silently replace research, ranges or an open estimate.
      if (
        data.estimate === undefined &&
        row.estimate &&
        JSON.stringify(row.estimate) !==
          JSON.stringify(
            taskEstimate({
              developmentHours: row.developmentHours,
              testingHours: row.testingHours,
            }),
          )
      )
        throw new Error('TASK_RELOAD_REQUIRED');
      const organization = {
        testInstructions:
          data.testInstructions === undefined ? (row.testInstructions ?? '') : inputText(data.testInstructions, 8000),
        assignee:
          data.assigneeUserID === undefined || data.assigneeUserID === (row.assignee?.userID ?? null)
            ? (row.assignee ?? null)
            : await taskAssignee(data.assigneeUserID),
        priority: priorityValue(data.priority === undefined ? (row.priority ?? 'medium') : data.priority),
        pinned: booleanValue(data.pinned === undefined ? (row.pinned ?? false) : data.pinned),
      };
      const before = publicTask(row);
      const changed = Object.entries(fields).some(
        ([key, value]) => JSON.stringify(before[key as keyof WorkTask]) !== JSON.stringify(value),
      );
      if (
        !changed &&
        before.priority === organization.priority &&
        before.pinned === organization.pinned &&
        before.billingHours === billingHours &&
        before.billingDate === billingDate &&
        before.testInstructions === organization.testInstructions &&
        JSON.stringify(before.assignee) === JSON.stringify(organization.assignee)
      )
        return JSON.stringify(before);
      await taskManager();
      return updateTask(row, access.userID, {
        ...fields,
        ...organization,
        ...billing,
        ...(changed
          ? {
              estimateRevision: row.estimateRevision + 1,
              approval: null,
              workflowStatus: 'acknowledged',
              status: legacyTaskStatus('acknowledged'),
            }
          : {}),
      });
    }
    if (data.expectedRevision !== undefined) throw new Error('TASK_INVALID_INPUT');
    const ids = data.feedbackCallIDs ?? [];
    if (!Array.isArray(ids) || ids.length > 20) throw new Error('TASK_INVALID_INPUT');
    const feedbackLinks: WorkTaskFeedbackLink[] = [];
    for (const id of [...new Set(ids.map(taskID))]) {
      const source = await feedbackSource(id);
      if (!feedbackLinks.some(link => link.callID === source.callID)) feedbackLinks.push(source);
    }
    const testInstructions = data.testInstructions === undefined ? '' : inputText(data.testInstructions, 8000);
    const assignee = data.assigneeUserID === undefined ? null : await taskAssignee(data.assigneeUserID);
    await taskManager();
    await ensureTaskStorage();
    const taskNumber = await allocateTaskNumber();
    const now = new Date().toISOString();
    const row: TaskRow = {
      _id: randomUUID(),
      taskNumber,
      ...fields,
      ...billing,
      testInstructions,
      assignee,
      priority: priorityValue(data.priority === undefined ? 'medium' : data.priority),
      pinned: booleanValue(data.pinned === undefined ? false : data.pinned),
      status: 'pending',
      workflowStatus: 'raised',
      revision: 1,
      estimateRevision: 1,
      feedbackLinks,
      approval: null,
      approvalHistory: [],
      archiveHistory: [],
      archivedAt: null,
      completedAt: null,
      billingDisposition: 'undecided',
      approvedBillableMinutes: null,
      billingAgreement: null,
      payment: null,
      productionCompletion: null,
      technicalProgress: { status: 'raised', revision: 1 },
      createdAt: now,
      updatedAt: now,
      createdBy: access.userID,
      updatedBy: access.userID,
    };
    const collection = await getRawCollection<TaskRow>(COLLECTION);
    await collection.insertOne(row);
    return JSON.stringify(publicTask(row));
  },
});
export const workTaskApprove = registerMutation({
  type: () => String,
  operationName: 'workTaskApprove',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'expectedRevision', type: () => Int },
    { name: 'approverName', type: () => String },
  ],
  resolve: (id: string, expectedRevision: number, approverNameArg: string) => async () => {
    const access = await taskAccess();
    const approverName = inputText(approverNameArg, 20, true);
    if (approverName !== 'Michael' && approverName !== 'Brenner') throw new Error('TASK_INVALID_INPUT');
    const row = await currentTask(id, expectedRevision);
    if (!row.kind || row.kind === 'unclassified') throw new Error('TASK_CLASSIFICATION_REQUIRED');
    if (!row.description.trim() || (row.kind === 'feature' && (estimateTotal(taskEstimate(row))?.max ?? 0) <= 0))
      throw new Error('TASK_ESTIMATE_REQUIRED');
    if (hasCurrentTaskApproval(row)) return JSON.stringify(publicTask(row));
    if ((row.approvalHistory ?? []).length >= 500) throw new Error('TASK_APPROVAL_HISTORY_FULL');
    await taskAccess();
    const approval: WorkTaskApproval = {
      byUserID: access.userID,
      byName: approverName,
      at: new Date().toISOString(),
      estimateRevision: row.estimateRevision,
    };
    return updateTask(row, access.userID, {
      approval,
      workflowStatus: 'approved',
      status: legacyTaskStatus('approved'),
      approvalHistory: [...(row.approvalHistory ?? []), { ...approval, snapshot: estimateSnapshot(row) }],
    });
  },
});
export const workTaskSetStatus = registerMutation({
  type: () => String,
  operationName: 'workTaskSetStatus',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'status', type: () => String },
    { name: 'expectedRevision', type: () => Int },
  ],
  resolve: (id: string, statusArg: string, expectedRevision: number) => async () => {
    const access = await taskManager();
    const status = inputText(statusArg, 40, true) as WorkTaskStatus;
    if (!WORK_TASK_STATUSES.includes(status)) throw new Error('TASK_RELOAD_REQUIRED');
    const row = await currentTask(id, expectedRevision);
    const change = taskTransition(row, status);
    if (taskWorkflowStatus(row) === status && !(change.approval === null && row.approval))
      return JSON.stringify(publicTask(row));
    if ((row.workflowHistory ?? []).length >= 1000) throw new Error('TASK_HISTORY_FULL');
    await taskManager();
    const at = new Date().toISOString();
    return updateTask(row, access.userID, {
      ...change,
      technicalProgress: { status, revision: row.revision + 1 },
      productionCompletion: status === 'production-completed' ? { at, byUserID: access.userID } : null,
      workflowHistory: [
        ...(row.workflowHistory ?? []),
        {
          from: taskWorkflowStatus(row),
          to: status,
          at,
          byUserID: access.userID,
        },
      ],
      ...(status === 'ready-for-production' ? { completedAt: at } : {}),
    });
  },
});

/** Commercial decisions are explicit and do not change delivery progress or old approvals. */
export const workTaskSetBilling = registerMutation({
  type: () => String,
  operationName: 'workTaskSetBilling',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'expectedRevision', type: () => Int },
    { name: 'disposition', type: () => String },
    { name: 'approvedMinutes', type: () => Int },
    { name: 'source', type: () => String },
    { name: 'agreedOn', type: () => String },
    { name: 'billingDate', type: () => String },
  ],
  resolve:
    (
      id: string,
      expectedRevision: number,
      dispositionArg: string,
      minutesArg: number,
      sourceArg: string,
      agreedOnArg: string,
      billingDateArg: string,
    ) =>
    async () => {
      const access = await taskManager();
      const disposition = inputText(dispositionArg, 30, true) as WorkTaskBillingDisposition;
      if (!['undecided', 'non-billable', 'billable'].includes(disposition)) throw new Error('TASK_INVALID_INPUT');
      if (Object.prototype.toString.call(minutesArg) !== '[object Number]') throw new Error('TASK_INVALID_INPUT');
      const minutes = Number(minutesArg);
      if (
        !Number.isSafeInteger(minutes) ||
        minutes < 0 ||
        minutes > 120000 ||
        (disposition === 'billable' ? minutes === 0 : minutes !== 0)
      )
        throw new Error('TASK_INVALID_INPUT');
      const source = inputText(sourceArg, 2000, disposition !== 'undecided');
      if (/\u007f/.test(source)) throw new Error('TASK_INVALID_INPUT');
      const dateText = inputText(agreedOnArg, 10);
      const agreedOn = dateText ? taskCalendarDate(dateText) : null;
      const billingDateText = inputText(billingDateArg, 10);
      const billingDate = billingDateText ? taskCalendarDate(billingDateText) : null;
      const row = await currentTask(id, expectedRevision);
      if (disposition === 'billable' && row.kind !== 'feature') throw new Error('TASK_INVALID_INPUT');
      if (row.archivedAt && disposition === 'billable' && !hasWorkTaskPayment(row))
        throw new Error('TASK_BILLING_UNPAID_ARCHIVE');
      const approvedMinutes = disposition === 'billable' ? minutes : null;
      const sameDecision =
        (row.billingDisposition ?? 'undecided') === disposition &&
        (row.approvedBillableMinutes ?? null) === approvedMinutes &&
        (disposition === 'undecided' ||
          (row.billingAgreement?.source === source && row.billingAgreement.agreedOn === agreedOn));
      if (sameDecision && (row.billingDate ?? null) === billingDate) return JSON.stringify(publicTask(row));
      if (row.payment && !sameDecision) throw new Error('TASK_BILLING_PAID_LOCKED');
      if (row.kind !== 'feature' && billingDate !== null) throw new Error('TASK_INVALID_INPUT');
      if ((row.billingHistory ?? []).length >= 1000) throw new Error('TASK_HISTORY_FULL');
      await taskManager();
      const at = new Date().toISOString();
      return updateTask(row, access.userID, {
        billingDisposition: disposition,
        approvedBillableMinutes: approvedMinutes,
        billingDate,
        billingAgreement: sameDecision
          ? (row.billingAgreement ?? null)
          : disposition === 'undecided'
            ? null
            : {
                source,
                agreedOn,
                recordedAt: at,
                recordedByUserID: access.userID,
              },
        billingHistory: [
          ...(row.billingHistory ?? []),
          {
            disposition,
            approvedMinutes,
            source,
            agreedOn,
            billingDate,
            at,
            byUserID: access.userID,
          },
        ],
      });
    },
});

/** Payment is an independently audited manual record, never inferred from task completion. */
export const workTaskSetPaid = registerMutation({
  type: () => String,
  operationName: 'workTaskSetPaid',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'paid', type: () => Boolean },
    { name: 'expectedRevision', type: () => Int },
  ],
  resolve: (id: string, paidArg: boolean, expectedRevision: number) => async () => {
    const access = await taskManager();
    const paid = booleanValue(paidArg);
    const row = await currentTask(id, expectedRevision);
    const minutes = approvedWorkTaskMinutes(publicTask(row));
    if (!paid && row.archivedAt && minutes !== null) throw new Error('TASK_BILLING_UNPAID_ARCHIVE');
    if (paid ? hasWorkTaskPayment(row) : !row.payment) return JSON.stringify(publicTask(row));
    if (paid && minutes === null) throw new Error('TASK_BILLING_AGREEMENT_REQUIRED');
    if ((row.paymentHistory ?? []).length >= 1000) throw new Error('TASK_HISTORY_FULL');
    await taskManager();
    const at = new Date().toISOString();
    return updateTask(row, access.userID, {
      payment: paid ? { paidAt: at, byUserID: access.userID } : null,
      paymentHistory: [
        ...(row.paymentHistory ?? []),
        { paid, approvedMinutes: minutes ?? 0, at, byUserID: access.userID },
      ],
    });
  },
});
/** Archive preserves delivery and financial data. An agreed charge must be paid first. */
export const workTaskSetArchived = registerMutation({
  type: () => String,
  operationName: 'workTaskSetArchived',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'archived', type: () => Boolean },
    { name: 'expectedRevision', type: () => Int },
  ],
  resolve: (id: string, archivedArg: boolean, expectedRevision: number) => async () => {
    const access = await taskManager();
    const archived = booleanValue(archivedArg);
    const row = await currentTask(id, expectedRevision);
    if (archived && approvedWorkTaskMinutes(publicTask(row)) !== null && !hasWorkTaskPayment(row))
      throw new Error('TASK_BILLING_UNPAID_ARCHIVE');
    if (Boolean(row.archivedAt) === archived) return JSON.stringify(publicTask(row));
    if ((row.archiveHistory ?? []).length >= 1000) throw new Error('TASK_HISTORY_FULL');
    await taskManager();
    const at = new Date().toISOString();
    return updateTask(row, access.userID, {
      archivedAt: archived ? at : null,
      archiveHistory: [...(row.archiveHistory ?? []), { archived, at, byUserID: access.userID }],
    });
  },
});
export const workTaskOrganize = registerMutation({
  type: () => String,
  operationName: 'workTaskOrganize',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'priority', type: () => String },
    { name: 'pinned', type: () => Boolean },
    { name: 'expectedRevision', type: () => Int },
  ],
  resolve: (id: string, priorityArg: string, pinnedArg: boolean, expectedRevision: number) => async () => {
    const access = await taskManager();
    const priority = priorityValue(inputText(priorityArg, 20, true)),
      pinned = booleanValue(pinnedArg);
    const row = await currentTask(id, expectedRevision);
    if ((row.priority ?? 'medium') === priority && (row.pinned === true) === pinned)
      return JSON.stringify(publicTask(row));
    return updateTask(row, access.userID, { priority, pinned });
  },
});
export const workTaskLinkFeedback = registerMutation({
  type: () => String,
  operationName: 'workTaskLinkFeedback',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'callID', type: () => String },
    { name: 'expectedRevision', type: () => Int },
  ],
  resolve: (id: string, callID: string, expectedRevision: number) => async () => {
    const access = await taskManager();
    const row = await currentTask(id, expectedRevision);
    const source = await feedbackSource(callID);
    if (row.feedbackLinks.some(link => link.callID === source.callID)) return JSON.stringify(publicTask(row));
    if (row.feedbackLinks.length >= 20) throw new Error('TASK_INVALID_INPUT');
    await taskManager();
    return updateTask(row, access.userID, {
      feedbackLinks: [...row.feedbackLinks, source],
      estimateRevision: row.estimateRevision + 1,
      approval: null,
      workflowStatus: 'acknowledged',
      status: legacyTaskStatus('acknowledged'),
    });
  },
});

export const workTaskBillingExport = registerQuery({
  type: () => String,
  operationName: 'workTaskBillingExport',
  params: [
    { name: 'fromDate', type: () => String },
    { name: 'toDate', type: () => String },
  ],
  resolve: (fromArg: string, toArg: string) => async () => {
    await taskAccess();
    const fromDate = inputText(fromArg, 10, true),
      toDate = inputText(toArg, 10, true);
    // Validate the range before any database or renderer work.
    buildWorkTaskBillingReport([], fromDate, toDate);
    await ensureTaskStorage();
    const collection = await getRawCollection<TaskRow>(COLLECTION);
    const rows: WorkTask[] = [];
    let after = '';
    while (true) {
      const batch = await collection
        .find(after ? { _id: { $gt: after } } : {}, {
          projection: {
            approvalHistory: 0,
            archiveHistory: 0,
            createdBy: 0,
            updatedBy: 0,
            feedbackLinks: 0,
            description: 0,
          },
          maxTimeMS: MAX_TIME_MS,
        })
        .sort({ _id: 1 })
        .limit(500)
        .toArray();
      if (!batch.length) break;
      rows.push(...batch.map(publicTask));
      if (rows.length > BILLING_EXPORT_LIMIT) throw new Error('TASK_BILLING_EXPORT_TOO_LARGE');
      after = batch[batch.length - 1]!._id;
    }
    const report = buildWorkTaskBillingReport(rows, fromDate, toDate);
    await taskAccess();
    const { renderWorkTaskBillingPdf } = await import('../server/work-task-billing-pdf.js');
    const pdf = await renderWorkTaskBillingPdf(report);
    return JSON.stringify({
      filename: `ZwickRoell-Features-${fromDate}-${toDate}.pdf`,
      mime: 'application/pdf',
      base64: pdf.toString('base64'),
      report,
    });
  },
});

/** Display-only translation: no migration, task update, approval change, or source text from the browser. */
export const workTaskTranslateToEnglish = registerQuery({
  type: () => String,
  operationName: 'workTaskTranslateToEnglish',
  params: [
    { name: 'taskID', type: () => String },
    { name: 'expectedRevision', type: () => Int },
  ],
  resolve: (id: string, expectedRevision: number) => async () => {
    const access = await taskAccess();
    const source = publicTask(await currentTask(id, expectedRevision, false));
    const { translateWorkTaskToEnglish, workTaskTranslationKey } = await import('../server/work-task-translation.js');
    const key = workTaskTranslationKey(source);
    const translated = await translateWorkTaskToEnglish(source, access.userID);
    const currentAccess = await taskAccess();
    if (currentAccess.userID !== access.userID) throw new Error('TASK_FORBIDDEN');
    const current = publicTask(await currentTask(id, expectedRevision, false));
    if (workTaskTranslationKey(current) !== key) throw new Error('TASK_CONFLICT');
    return JSON.stringify(translated);
  },
});
